import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import { toastMessageAlert } from "../../utils/alert";

export default function ScheduleBoard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { event_id } = useParams();
  const initialGender = (searchParams.get("gender") || "all").toLowerCase();
  const initialDate = searchParams.get("date") || new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(initialDate);
  const [gender, setGender] = useState(initialGender);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [downloading, setDownloading] = useState(false);
  const gameDayContextQuery = useMemo(() => {
    const q = new URLSearchParams();
    if (date) q.set("date", date);
    if (event_id) q.set("event_id", String(event_id));
    q.set("gender", gender || "all");
    return q.toString();
  }, [date, event_id, gender]);
  const activeGenderLabel =
    gender === "male" || gender === "men" ? "Male" : gender === "female" || gender === "women" ? "Female" : "All";

  React.useEffect(() => {
    const nextGender = (searchParams.get("gender") || "all").toLowerCase();
    const nextDate = searchParams.get("date");
    setGender((prev) => (prev === nextGender ? prev : nextGender));
    if (nextDate) {
      setDate((prev) => (prev === nextDate ? prev : nextDate));
    }
  }, [searchParams]);

  React.useEffect(() => {
    if (!message) return;
    toastMessageAlert(message, { timer: 2400 });
  }, [message]);

  const downloadReport = async (path, fallbackName, format = "pdf", params = {}) => {
    setDownloading(true);
    setMessage("");
    try {
      const res = await API.get(path, { responseType: "blob", params: { ...params, format } });
      const blob = res?.data;
      if (!blob) throw new Error("Empty file response.");

      const disposition =
        res?.headers?.["content-disposition"] ||
        res?.headers?.["Content-Disposition"] ||
        "";
      const contentType =
        (
          res?.headers?.["content-type"] ||
          res?.headers?.["Content-Type"] ||
          blob?.type ||
          ""
        ).toLowerCase();

      let fileName = fallbackName;
      const match = /filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/.exec(disposition);
      if (match?.[1]) {
        fileName = decodeURIComponent(match[1]);
      }
      if (format === "excel" && contentType.includes("text/csv")) {
        if (/\.xlsx$/i.test(fileName)) {
          fileName = fileName.replace(/\.xlsx$/i, ".csv");
        } else if (!/\.csv$/i.test(fileName)) {
          fileName = `${fileName}.csv`;
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setMessage(err?.message || "Failed to download report.");
    } finally {
      setDownloading(false);
    }
  };

  const autoGenerate = async () => {
    setBusy(true);
    setMessage("");
    try {
      const res = await API.post("/admin/schedule/auto", {
        event_id,
        date,
      });
      setMessage(res?.message || "Schedule generated successfully.");
    } catch (err) {
      setMessage(err?.message || "Failed to auto-generate schedule.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Schedule Board</h1>
            <button
              onClick={() => navigate(`/admin/game-day?${gameDayContextQuery}`)}
              className="text-sm px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
            >
              Back to Game Day
            </button>
          </div>

          <div className="bg-white rounded-xl shadow p-5 space-y-4">
            <p className="text-sm text-gray-600">Event ID: <span className="font-semibold">{event_id}</span></p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2.5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2.5"
                >
                  <option value="all">All</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
            <p className="text-xs text-blue-700">Active Filter: Gender = {activeGenderLabel}</p>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={autoGenerate}
                disabled={busy}
                className="bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                Auto Generate Schedule
              </button>
              <button
                onClick={() =>
                  downloadReport(
                    `/admin/game-day/heat-schedule/${event_id}`,
                    `heat_schedule_event_${event_id}.pdf`,
                    "pdf",
                    { gender }
                  )
                }
                disabled={downloading || !event_id}
                className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg hover:bg-emerald-700 disabled:bg-gray-400"
              >
                Heat Schedule PDF
              </button>
              <button
                onClick={() =>
                  downloadReport(
                    `/admin/game-day/heat-schedule/${event_id}`,
                    `heat_schedule_event_${event_id}.xlsx`,
                    "excel",
                    { gender }
                  )
                }
                disabled={downloading || !event_id}
                className="bg-emerald-800 text-white px-5 py-2.5 rounded-lg hover:bg-emerald-900 disabled:bg-gray-400"
              >
                Heat Schedule Excel
              </button>
              <button
                onClick={() =>
                  downloadReport(
                    `/admin/game-day/master-schedule/${date}`,
                    `master_schedule_${date}.pdf`,
                    "pdf",
                    { gender }
                  )
                }
                disabled={downloading || !date}
                className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
              >
                Master Schedule PDF
              </button>
              <button
                onClick={() =>
                  downloadReport(
                    `/admin/game-day/master-schedule/${date}`,
                    `master_schedule_${date}.xlsx`,
                    "excel",
                    { gender }
                  )
                }
                disabled={downloading || !date}
                className="bg-indigo-800 text-white px-5 py-2.5 rounded-lg hover:bg-indigo-900 disabled:bg-gray-400"
              >
                Master Schedule Excel
              </button>
            </div>

            {message ? <p className="text-sm text-gray-700 bg-gray-100 rounded-lg p-3">{message}</p> : null}
          </div>
        </div>
      </div>
    </>
  );
}
